import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI, quotesAPI, invoicesAPI } from '../lib/api';
import { usePermissions } from '../contexts/PermissionContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Edit, Trash2, Plus, MapPin, Phone, Mail, Globe, Building2, DollarSign, FileText, Briefcase, Receipt, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { InvalidRoute, isValidId } from '../components/ui/page-guards';
import { useAsyncAction, useFormSubmit, SubmitButton, DeleteButton } from '../components/ui/action-button';
import { format } from 'date-fns';
import { useCurrency } from '../contexts/CurrencyContext';

export default function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currency = useCurrency();
  const { hasPermission } = usePermissions();
  
  // Route guard - validate ID before any operations
  if (!isValidId(id)) {
    return (
      <InvalidRoute
        title="Invalid Client"
        message="The client ID is missing or invalid. Please select a valid client from the list."
        backPath="/clients"
        backLabel="Back to Clients"
      />
    );
  }
  
  // Permission checks
  const canUpdate = hasPermission('client', 'client', 'update');
  const canDelete = hasPermission('client', 'client', 'delete');
  
  // Client state
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [historyTab, setHistoryTab] = useState('quotes');
  
  // Address state
  const [addresses, setAddresses] = useState([]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'USA',
    address_type: 'billing'
  });
  
  // History state
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  
  // Edit client dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    industry: '',
    website: '',
    notes: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: ''
  });

  useEffect(() => {
    loadClient();
    loadHistory();
  }, [id]);

  const loadClient = async () => {
    try {
      const response = await clientsAPI.getOne(id);
      const clientData = response.data;
      setClient(clientData);
      
      // Set edit form data
      setEditForm({
        name: clientData.name || '',
        email: clientData.email || '',
        phone: clientData.phone || '',
        company_name: clientData.company_name || '',
        industry: clientData.industry || '',
        website: clientData.website || '',
        notes: clientData.notes || '',
        address: clientData.address || '',
        city: clientData.city || '',
        state: clientData.state || '',
        zip_code: clientData.zip_code || '',
        country: clientData.country || ''
      });
      
      // Set addresses from API response
      if (clientData.addresses && Array.isArray(clientData.addresses)) {
        setAddresses(clientData.addresses);
      } else {
        setAddresses([]);
      }
    } catch (error) {
      console.error('Failed to load client:', error);
      toast.error('Failed to load client details');
      setClient(null);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      // Load quotes for this client
      const quotesRes = await quotesAPI.getAll({ client_id: id });
      const quotesRaw = quotesRes.data;
      const quotesData = Array.isArray(quotesRaw) ? quotesRaw : (quotesRaw?.items || []);
      setQuotes(quotesData);
      
      // Load invoices for this client
      const invoicesRes = await invoicesAPI.getAll({ client_id: id });
      const invoicesRaw = invoicesRes.data;
      const invoicesData = Array.isArray(invoicesRaw) ? invoicesRaw : (invoicesRaw?.items || []);
      setInvoices(invoicesData);
      
      // Fetch payments from quotes and invoices
      let allPayments = [];
      
      // Fetch payments for each quote
      if (quotesData.length > 0) {
        const quotePaymentsPromises = quotesData.map(async (quote) => {
          try {
            const response = await quotesAPI.getPayments(quote.id);
            const payments = response?.data || [];
            return payments.map(p => ({ 
              ...p, 
              type: 'Quote', 
              source_id: quote.id, 
              source_number: quote.quote_number,
              client_name: quote.client_name
            }));
          } catch (error) {
            console.error(`Failed to fetch payments for quote ${quote.id}:`, error);
            return [];
          }
        });
        const quotePaymentsArrays = await Promise.all(quotePaymentsPromises);
        allPayments = [...allPayments, ...quotePaymentsArrays.flat()];
      }
      
      // Fetch payments for each invoice
      if (invoicesData.length > 0) {
        const invoicePaymentsPromises = invoicesData.map(async (invoice) => {
          try {
            const response = await invoicesAPI.getPayments(invoice.id);
            const payments = response?.data || [];
            return payments.map(p => ({ 
              ...p, 
              type: 'Invoice', 
              source_id: invoice.id, 
              source_number: invoice.invoice_number,
              client_name: invoice.client_name
            }));
          } catch (error) {
            console.error(`Failed to fetch payments for invoice ${invoice.id}:`, error);
            return [];
          }
        });
        const invoicePaymentsArrays = await Promise.all(invoicePaymentsPromises);
        allPayments = [...allPayments, ...invoicePaymentsArrays.flat()];
      }
      
      setPayments(allPayments);
    } catch (error) {
      console.error('Failed to load client history:', error);
      // Don't show error toast, just use empty arrays
    }
  };

  // Client loading is handled by useEffect below

  const { handleSubmit: handleUpdateClient, isSubmitting: updatingClient } = useFormSubmit(
    async () => {
      await clientsAPI.update(id, editForm);
      await loadClient();
      setEditDialogOpen(false);
    },
    { successMessage: 'Client updated successfully', errorMessage: 'Failed to update client' }
  );

  const [executeAddAddress, addingAddress] = useAsyncAction(async () => {
    if (editingAddress) {
      // Update address via API
      const response = await clientsAPI.updateAddress(id, editingAddress.id, addressForm);
      setClient(response.data);
      setAddresses(response.data.addresses || []);
    } else {
      // Add new address via API
      const response = await clientsAPI.addAddress(id, addressForm);
      setClient(response.data);
      setAddresses(response.data.addresses || []);
    }
    setAddressDialogOpen(false);
    setEditingAddress(null);
    setAddressForm({
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'USA',
      address_type: 'billing'
    });
  }, editingAddress ? 'Address updated' : 'Address added', 'Failed to save address');

  const handleAddAddress = async (e) => {
    e.preventDefault();
    await executeAddAddress();
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || 'USA',
      address_type: address.address_type || 'billing'
    });
    setAddressDialogOpen(true);
  };

  const [executeDeleteAddress, deletingAddress] = useAsyncAction(async (addressId) => {
    const response = await clientsAPI.deleteAddress(id, addressId);
    setClient(response.data);
    setAddresses(response.data.addresses || []);
  }, 'Address deleted', 'Failed to delete address');

  const handleDeleteAddress = async (addressId) => {
    await executeDeleteAddress(addressId);
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="client-details-loading">
        <div className="h-8 w-64 bg-muted animate-pulse rounded"></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="h-96 bg-muted animate-pulse rounded"></div>
          <div className="col-span-2 h-96 bg-muted animate-pulse rounded"></div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-4" data-testid="client-not-found">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Clients
          </Button>
        </div>
        <Card className="p-8 text-center">
          <CardContent>
            <h2 className="text-2xl font-bold mb-2">Client Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The client you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <Button onClick={() => navigate('/clients')}>
              Go Back to Clients
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalRevenue = invoices.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0);
  const paidAmount = invoices.reduce((sum, inv) => sum + (parseFloat(inv.paid_amount) || 0), 0);
  const pendingAmount = totalRevenue - paidAmount;

  return (
    <div className="space-y-4" data-testid="client-details-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Clients
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{client.name}</h1>
            <p className="text-muted-foreground">{client.company_name || 'No company'}</p>
          </div>
        </div>
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          {canUpdate && (
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="edit-client-button">
                <Edit className="h-4 w-4 mr-2" />
                Edit Client
              </Button>
            </DialogTrigger>
          )}
          <DialogContent data-testid="edit-client-dialog">
            <DialogHeader>
              <DialogTitle>Edit Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateClient} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                    data-testid="edit-name-input"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    required
                    data-testid="edit-email-input"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    data-testid="edit-phone-input"
                  />
                </div>
                <div>
                  <Label>Company Name</Label>
                  <Input
                    value={editForm.company_name}
                    onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                    data-testid="edit-company-input"
                  />
                </div>
                <div>
                  <Label>Industry</Label>
                  <Input
                    value={editForm.industry}
                    onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                    data-testid="edit-industry-input"
                  />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    value={editForm.website}
                    onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                    data-testid="edit-website-input"
                  />
                </div>
              </div>
              
              {/* Permanent Address Fields */}
              <div className="space-y-3 pt-3 border-t">
                <h3 className="text-sm font-semibold">Official Permanent Address</h3>
                <div>
                  <Label>Address</Label>
                  <Textarea
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    rows={2}
                    placeholder="123 Main Street, Suite 100"
                    data-testid="edit-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      placeholder="New York"
                      data-testid="edit-city-input"
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={editForm.state}
                      onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                      placeholder="NY"
                      data-testid="edit-state-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Zip Code</Label>
                    <Input
                      value={editForm.zip_code}
                      onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })}
                      placeholder="10001"
                      data-testid="edit-zipcode-input"
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input
                      value={editForm.country}
                      onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                      placeholder="United States"
                      data-testid="edit-country-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  data-testid="edit-notes-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <SubmitButton type="submit" isSubmitting={updatingClient} loadingText="Saving..." data-testid="save-client-button">
                  Save Changes
                </SubmitButton>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Sidebar - Client Info */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <p className="text-sm">{client.email}</p>
                  </div>
                </div>
                {client.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Phone</Label>
                      <p className="text-sm">{client.phone}</p>
                    </div>
                  </div>
                )}
                {client.company_name && (
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Company</Label>
                      <p className="text-sm">{client.company_name}</p>
                    </div>
                  </div>
                )}
                {client.industry && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Industry</Label>
                      <p className="text-sm">{client.industry}</p>
                    </div>
                  </div>
                )}
                {client.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Website</Label>
                      <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                        {client.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Financial Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Total Revenue</Label>
                <p className="text-2xl font-bold text-primary">{currency.format(totalRevenue)}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Paid</Label>
                  <p className="text-lg font-semibold text-green-600">{currency.format(paidAmount)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Pending</Label>
                  <p className="text-lg font-semibold text-orange-600">{currency.format(pendingAmount)}</p>
                </div>
              </div>
              <div className="pt-2 border-t space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Quotes</span>
                  <span className="font-medium">{quotes.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Invoices</span>
                  <span className="font-medium">{invoices.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Payments</span>
                  <span className="font-medium">{payments.length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Content - Tabs */}
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="addresses">Addresses ({addresses.length})</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Client Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Created Date</Label>
                      <p className="text-sm text-muted-foreground">
                        {client.created_at ? format(new Date(client.created_at), 'MMM dd, yyyy') : '-'}
                      </p>
                    </div>
                    <div>
                      <Label>Last Updated</Label>
                      <p className="text-sm text-muted-foreground">
                        {client.updated_at ? format(new Date(client.updated_at), 'MMM dd, yyyy') : '-'}
                      </p>
                    </div>
                  </div>
                  {client.notes && (
                    <div>
                      <Label>Notes</Label>
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{client.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Official Permanent Address */}
              {(client.address || client.city || client.state || client.zip_code || client.country) && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-muted-foreground" />
                      Official Permanent Address
                    </CardTitle>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditForm({
                          ...editForm,
                          address: client.address || '',
                          city: client.city || '',
                          state: client.state || '',
                          zip_code: client.zip_code || '',
                          country: client.country || ''
                        });
                        setEditDialogOpen(true);
                      }}
                      data-testid="edit-permanent-address-button"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {client.address && (
                      <p className="text-sm" data-testid="client-address-display">{client.address}</p>
                    )}
                    <div className="flex flex-wrap gap-1 text-sm text-muted-foreground">
                      {client.city && <span data-testid="client-city-display">{client.city}</span>}
                      {client.city && client.state && <span>,</span>}
                      {client.state && <span data-testid="client-state-display">{client.state}</span>}
                      {client.zip_code && <span data-testid="client-zipcode-display">{client.zip_code}</span>}
                    </div>
                    {client.country && (
                      <p className="text-sm text-muted-foreground" data-testid="client-country-display">{client.country}</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Addresses Tab */}
            <TabsContent value="addresses" className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Addresses</CardTitle>
                  <Dialog open={addressDialogOpen} onOpenChange={(open) => {
                    setAddressDialogOpen(open);
                    if (!open) {
                      setEditingAddress(null);
                      setAddressForm({
                        address_line1: '',
                        address_line2: '',
                        city: '',
                        state: '',
                        postal_code: '',
                        country: 'USA',
                        address_type: 'billing'
                      });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="add-address-button">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Address
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="address-dialog">
                      <DialogHeader>
                        <DialogTitle>{editingAddress ? 'Edit Address' : 'Add New Address'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddAddress} className="space-y-4">
                        <div>
                          <Label>Address Line 1 *</Label>
                          <Input
                            value={addressForm.address_line1}
                            onChange={(e) => setAddressForm({ ...addressForm, address_line1: e.target.value })}
                            placeholder="123 Main Street"
                            required
                            data-testid="address-line1-input"
                          />
                        </div>
                        <div>
                          <Label>Address Line 2</Label>
                          <Input
                            value={addressForm.address_line2}
                            onChange={(e) => setAddressForm({ ...addressForm, address_line2: e.target.value })}
                            placeholder="Suite 100, Floor 2"
                            data-testid="address-line2-input"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>City *</Label>
                            <Input
                              value={addressForm.city}
                              onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                              placeholder="New York"
                              required
                              data-testid="address-city-input"
                            />
                          </div>
                          <div>
                            <Label>State/Province *</Label>
                            <Input
                              value={addressForm.state}
                              onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                              placeholder="NY"
                              required
                              data-testid="address-state-input"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Postal Code *</Label>
                            <Input
                              value={addressForm.postal_code}
                              onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                              placeholder="10001"
                              required
                              data-testid="address-postal-input"
                            />
                          </div>
                          <div>
                            <Label>Country *</Label>
                            <Input
                              value={addressForm.country}
                              onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                              placeholder="USA"
                              required
                              data-testid="address-country-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Type *</Label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                            value={addressForm.address_type}
                            onChange={(e) => setAddressForm({ ...addressForm, address_type: e.target.value })}
                            data-testid="address-type-select"
                          >
                            <option value="billing">Billing</option>
                            <option value="shipping">Shipping</option>
                            <option value="both">Both</option>
                          </select>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => setAddressDialogOpen(false)}>
                            Cancel
                          </Button>
                          <SubmitButton type="submit" isSubmitting={addingAddress} loadingText="Saving..." data-testid="save-address-button">
                            {editingAddress ? 'Update Address' : 'Add Address'}
                          </SubmitButton>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {addresses.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No addresses added yet</p>
                  ) : (
                    <div className="space-y-3">
                      {addresses.map((address) => (
                        <Card key={address.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs">
                                    {address.address_type}
                                  </Badge>
                                </div>
                                <p className="text-sm font-medium">{address.address_line1}</p>
                                {address.address_line2 && (
                                  <p className="text-sm">{address.address_line2}</p>
                                )}
                                <p className="text-sm text-muted-foreground">
                                  {address.city}, {address.state} {address.postal_code}
                                </p>
                                <p className="text-sm text-muted-foreground">{address.country}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditAddress(address)}
                                data-testid={`edit-address-${address.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAddress(address.id)}
                                data-testid={`delete-address-${address.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="space-y-4">
              <Tabs value={historyTab} onValueChange={setHistoryTab}>
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="quotes">Quotes ({quotes.length})</TabsTrigger>
                  <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
                  <TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger>
                </TabsList>

                {/* Quotes Tab */}
                <TabsContent value="quotes" className="space-y-2">
                  {quotes.length === 0 ? (
                    <Card className="p-8 text-center">
                      <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No quotes found for this client</p>
                    </Card>
                  ) : (
                    quotes.map((quote) => (
                      <Card key={quote.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <p className="font-semibold">Quote #{quote.quote_number || quote.id.slice(0, 8)}</p>
                                <Badge variant="outline">{quote.status}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                {quote.description || 'No description'}
                              </p>
                              <p className="text-xs text-muted-foreground mt-2">
                                Created: {quote.created_at ? format(new Date(quote.created_at), 'MMM dd, yyyy') : '-'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-primary">
                                {currency.format(parseFloat(quote.total_amount || 0))}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>

                {/* Invoices Tab */}
                <TabsContent value="invoices" className="space-y-2">
                  {invoices.length === 0 ? (
                    <Card className="p-8 text-center">
                      <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No invoices found for this client</p>
                    </Card>
                  ) : (
                    invoices.map((invoice) => (
                      <Card key={invoice.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Receipt className="h-4 w-4 text-muted-foreground" />
                                <p className="font-semibold">Invoice #{invoice.invoice_number || invoice.id.slice(0, 8)}</p>
                                <Badge variant="outline">{invoice.status}</Badge>
                              </div>
                              <div className="mt-2 space-y-1">
                                <p className="text-sm">
                                  <span className="text-muted-foreground">Total:</span>{' '}
                                  <span className="font-semibold">{currency.format(parseFloat(invoice.total_amount || 0))}</span>
                                </p>
                                <p className="text-sm">
                                  <span className="text-muted-foreground">Paid:</span>{' '}
                                  <span className="font-semibold text-green-600">{currency.format(parseFloat(invoice.paid_amount || 0))}</span>
                                </p>
                                <p className="text-sm">
                                  <span className="text-muted-foreground">Balance:</span>{' '}
                                  <span className="font-semibold text-orange-600">
                                    {currency.format(parseFloat(invoice.total_amount || 0) - parseFloat(invoice.paid_amount || 0))}
                                  </span>
                                </p>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                Issued: {invoice.issue_date ? format(new Date(invoice.issue_date), 'MMM dd, yyyy') : '-'}
                                {invoice.due_date && ` • Due: ${format(new Date(invoice.due_date), 'MMM dd, yyyy')}`}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>

                {/* Payments Tab */}
                <TabsContent value="payments" className="space-y-2">
                  {payments.length === 0 ? (
                    <Card className="p-8 text-center">
                      <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No payments found for this client</p>
                    </Card>
                  ) : (
                    payments.map((payment, index) => (
                      <Card key={index}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <CreditCard className="h-4 w-4 text-muted-foreground" />
                                <p className="font-semibold">
                                  Payment for {payment.type} #{payment.source_number || payment.source_id?.slice(0, 8)}
                                </p>
                                <Badge variant="outline">{payment.payment_method || 'N/A'}</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                Date: {payment.payment_date ? format(new Date(payment.payment_date), 'MMM dd, yyyy') : '-'}
                              </p>
                              {payment.notes && (
                                <p className="text-sm text-muted-foreground mt-1">{payment.notes}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-green-600">
                                {currency.format(parseFloat(payment.amount || 0))}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
